import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiClient from "@/utils/apiClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { ArrowLeft, Calendar, Heart, Activity, FileText, User, Phone, Mail, MapPin } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const getPhotoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const backendBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  return `${backendBase}${url.startsWith('/') ? '' : '/'}${url}`;
};

const StudentProfile = ({ permissions }) => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [healthRecords, setHealthRecords] = useState([]);
  const [activities, setActivities] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuickAction, setShowQuickAction] = useState(null);
  const [actionData, setActionData] = useState({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    fetchStudentData();
  }, [studentId]);

  const fetchStudentData = async () => {
    try {
      let studentData = null;
      try {
        const studentRes = await apiClient.get(`/v1/warden/students/${studentId}`);
        if (studentRes.data && !studentRes.data.error) {
          studentData = studentRes.data;
        }
      } catch (err) {
        // Fallback to my-students list
      }

      if (!studentData) {
        const studentsRes = await apiClient.get('/v1/warden/students/my-students');
        studentData = (studentsRes.data.students || []).find(s => s.id === studentId);
      }
      setStudent(studentData);

      const attRes = await apiClient.get(`/v1/warden/students/${studentId}/attendance`);
      setAttendance(attRes.data || []);

      const healthRes = await apiClient.get(`/v1/warden/students/${studentId}/health`);
      setHealthRecords(healthRes.data || []);

      const actRes = await apiClient.get(`/v1/warden/students/${studentId}/activities`);
      setActivities(actRes.data || []);

      if (permissions?.can_update_results || true) {
        const resultsRes = await apiClient.get(`/v1/warden/students/${studentId}/results`);
        setResults(resultsRes.data || []);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to load student data");
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = async (e, forceUpdate = false) => {
    e.preventDefault();
    try {
      if (showQuickAction === 'attendance') {
        await apiClient.post('/v1/warden/attendance/mark', {
          student_id: studentId,
          date: actionData.date,
          status: actionData.status
        });
        toast.success("Attendance marked");
      } else if (showQuickAction === 'health') {
        const payload = {
          student_id: studentId,
          date: actionData.date,
          status: actionData.customStatus || actionData.status,
          symptoms: actionData.symptoms || '',
          treatment: actionData.treatment || '',
          follow_up_required: actionData.follow_up_required || false,
          force_update: forceUpdate
        };
        const response = await apiClient.post('/v1/warden/health/add', payload);
        
        // Check if duplicate found
        if (response.data.duplicate_found && !forceUpdate) {
          setDuplicateInfo({
            type: 'health',
            existing: response.data.existing_record,
            message: response.data.message
          });
          setShowConfirmDialog(true);
          return;
        }
        
        toast.success("Health record added");
      } else if (showQuickAction === 'activity') {
        const payload = {
          student_id: studentId,
          date: actionData.date,
          activity_type: actionData.activity_type,
          activity_name: actionData.activity_name,
          description: actionData.description || '',
          remarks: actionData.remarks || '',
          force_update: forceUpdate
        };
        const response = await apiClient.post('/v1/warden/activities/add', payload);
        
        // Check if duplicate found
        if (response.data.duplicate_found && !forceUpdate) {
          setDuplicateInfo({
            type: 'activity',
            existing: response.data.existing_record,
            message: response.data.message
          });
          setShowConfirmDialog(true);
          return;
        }
        
        toast.success("Activity added");
      }
      setShowQuickAction(null);
      setActionData({});
      setShowConfirmDialog(false);
      setDuplicateInfo(null);
      fetchStudentData();
    } catch (error) {
      console.error('Quick action error:', error);
      toast.error(error.response?.data?.detail || "Action failed");
    }
  };

  const handleConfirmOverwrite = (e) => {
    setShowConfirmDialog(false);
    handleQuickAction(e, true);
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  if (!student) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6 text-center">
            <p>Student not found or not assigned to you</p>
            <Button onClick={() => navigate('/warden/students')} className="mt-4">Go Back</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate('/warden/students')}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="text-2xl font-bold" style={{ color: "#2C5F7F" }}>Student Profile</h1>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-6">
            <Avatar className="w-24 h-24 border-4 border-blue-50 shadow-md">
              <AvatarImage src={getPhotoUrl(student.photo_url)} alt={student.name} className="object-cover" />
              <AvatarFallback className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-3xl font-bold">
                {student.name.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-2">{student.name}</h2>
              <div className="grid md:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-500" />
                  <span>Class: {student.class_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <span>Room: {student.room_number || 'Not assigned'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-500" />
                  <span>{student.primary_phone}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span>{student.parent_email || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            {permissions?.can_update_attendance && (
              <Button variant="outline" onClick={() => { setShowQuickAction('attendance'); setActionData({ date: today, status: 'present' }); }}>
                <Calendar className="w-4 h-4 mr-2" />
                Mark Attendance
              </Button>
            )}
            {permissions?.can_update_health && (
              <Button variant="outline" onClick={() => { setShowQuickAction('health'); setActionData({ date: today, status: 'healthy', symptoms: '', treatment: '' }); }}>
                <Heart className="w-4 h-4 mr-2" />
                Add Health
              </Button>
            )}
            {permissions?.can_update_activities && (
              <Button variant="outline" onClick={() => { setShowQuickAction('activity'); setActionData({ date: today, activity_type: 'sports', activity_name: '', description: '' }); }}>
                <Activity className="w-4 h-4 mr-2" />
                Add Activity
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="attendance">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="attendance">Attendance</TabsTrigger>
          <TabsTrigger value="health">Health</TabsTrigger>
          <TabsTrigger value="activities">Activities</TabsTrigger>
          <TabsTrigger value="results">Results</TabsTrigger>
        </TabsList>

        <TabsContent value="attendance">
          <Card>
            <CardHeader>
              <CardTitle>Attendance History</CardTitle>
            </CardHeader>
            <CardContent>
              {attendance.length === 0 ? (
                <p className="text-gray-500">No attendance records</p>
              ) : (
                <div className="space-y-2">
                  {attendance.slice(0, 30).map((record, idx) => (
                    <div key={idx} className="flex justify-between items-center p-3 border rounded">
                      <span>{record.date}</span>
                      <span className={`px-3 py-1 rounded text-sm ${
                        record.status === 'present' ? 'bg-green-100 text-green-700' :
                        record.status === 'absent' ? 'bg-red-100 text-red-700' :
                        record.status === 'half-day' ? 'bg-yellow-100 text-yellow-700' :
                        record.status === 'holiday' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>{record.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="health">
          <Card>
            <CardHeader>
              <CardTitle>Health Records</CardTitle>
            </CardHeader>
            <CardContent>
              {healthRecords.length === 0 ? (
                <p className="text-gray-500">No health records</p>
              ) : (
                <div className="space-y-3">
                  {healthRecords.map((record) => (
                    <Card key={record.id}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-medium">{record.date}</span>
                          <span className={`px-2 py-1 rounded text-xs ${
                            record.status === 'sick' ? 'bg-red-100 text-red-700' :
                            record.status === 'injured' ? 'bg-orange-100 text-orange-700' :
                            record.status === 'healthy' ? 'bg-green-100 text-green-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>{record.status}</span>
                        </div>
                        {record.symptoms && <p className="text-sm text-gray-600"><strong>Symptoms:</strong> {record.symptoms}</p>}
                        {record.treatment && <p className="text-sm text-gray-600"><strong>Treatment:</strong> {record.treatment}</p>}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activities">
          <Card>
            <CardHeader>
              <CardTitle>Activities</CardTitle>
            </CardHeader>
            <CardContent>
              {activities.length === 0 ? (
                <p className="text-gray-500">No activities recorded</p>
              ) : (
                <div className="space-y-3">
                  {activities.map((activity) => (
                    <Card key={activity.id}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-medium">{activity.activity_name}</span>
                          <span className="text-xs text-gray-500">{activity.date}</span>
                        </div>
                        <p className="text-xs text-gray-600">{activity.activity_type}</p>
                        {activity.description && <p className="text-sm mt-2">{activity.description}</p>}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="results">
          <Card>
            <CardHeader>
              <CardTitle>Academic Results</CardTitle>
            </CardHeader>
            <CardContent>
              {results.length === 0 ? (
                <p className="text-gray-500">No results available</p>
              ) : (
                <div className="space-y-3">
                  {results.map((result) => (
                    <Card key={result.id}>
                      <CardContent className="p-4">
                        <h4 className="font-semibold mb-2">{result.exam_type}</h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {result.subjects?.map((sub, idx) => (
                            <div key={idx} className="flex justify-between">
                              <span>{sub.subject_name}:</span>
                              <span className="font-medium">{sub.marks ?? sub.marks_obtained ?? 0}/{sub.max_marks ?? sub.total_marks ?? 100}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Quick Action Dialogs */}
      <Dialog open={showQuickAction !== null} onOpenChange={() => setShowQuickAction(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {showQuickAction === 'attendance' && 'Mark Attendance'}
              {showQuickAction === 'health' && 'Add Health Record'}
              {showQuickAction === 'activity' && 'Add Activity'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleQuickAction} className="space-y-4">
            {showQuickAction === 'attendance' && (
              <>
                <div>
                  <Label>Date</Label>
                  <Input type="date" max={today} value={actionData.date} onChange={(e) => setActionData({...actionData, date: e.target.value})} required />
                </div>
                <div>
                  <Label>Status</Label>
                  <select className="w-full px-3 py-2 border rounded" value={actionData.status} onChange={(e) => setActionData({...actionData, status: e.target.value})} required>
                    <option value="present">Present</option>
                    <option value="absent">Absent</option>
                    <option value="half-day">Half-day</option>
                    <option value="leave">Leave</option>
                    <option value="holiday">Holiday</option>
                  </select>
                </div>
              </>
            )}
            {showQuickAction === 'health' && (
              <>
                <div>
                  <Label>Date</Label>
                  <Input type="date" max={today} value={actionData.date} onChange={(e) => setActionData({...actionData, date: e.target.value})} required />
                </div>
                <div>
                  <Label>Status</Label>
                  <select className="w-full px-3 py-2 border rounded" value={actionData.status} onChange={(e) => setActionData({...actionData, status: e.target.value, customStatus: ''})} required>
                    <option value="healthy">Healthy</option>
                    <option value="sick">Sick</option>
                    <option value="injured">Injured</option>
                    <option value="unwell">Unwell</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                {actionData.status === 'custom' && (
                  <div>
                    <Label>Custom Health Status</Label>
                    <Input value={actionData.customStatus || ''} onChange={(e) => setActionData({...actionData, customStatus: e.target.value})} placeholder="Enter custom status" required />
                  </div>
                )}
                <div>
                  <Label>Symptoms {actionData.status !== 'healthy' && '*'}</Label>
                  <Textarea value={actionData.symptoms} onChange={(e) => setActionData({...actionData, symptoms: e.target.value})} required={actionData.status !== 'healthy'} />
                </div>
                <div>
                  <Label>Treatment</Label>
                  <Textarea value={actionData.treatment} onChange={(e) => setActionData({...actionData, treatment: e.target.value})} />
                </div>
              </>
            )}
            {showQuickAction === 'activity' && (
              <>
                <div>
                  <Label>Date</Label>
                  <Input type="date" max={today} value={actionData.date} onChange={(e) => setActionData({...actionData, date: e.target.value})} required />
                </div>
                <div>
                  <Label>Type</Label>
                  <select className="w-full px-3 py-2 border rounded" value={actionData.activity_type} onChange={(e) => setActionData({...actionData, activity_type: e.target.value})} required>
                    <option value="sports">Sports</option>
                    <option value="cultural">Cultural</option>
                    <option value="academic">Academic</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <Label>Activity Name</Label>
                  <Input value={actionData.activity_name} onChange={(e) => setActionData({...actionData, activity_name: e.target.value})} required />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea value={actionData.description} onChange={(e) => setActionData({...actionData, description: e.target.value})} />
                </div>
              </>
            )}
            <Button type="submit" className="w-full btn-primary text-white">Submit</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog for Duplicates */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Overwrite Existing Record?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">{duplicateInfo?.message}</p>
            {duplicateInfo?.existing && (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                <p className="font-semibold text-sm mb-2">Existing Record:</p>
                {duplicateInfo.type === 'health' && (
                  <div className="text-sm space-y-1">
                    <p><strong>Status:</strong> {duplicateInfo.existing.status}</p>
                    <p><strong>Symptoms:</strong> {duplicateInfo.existing.symptoms || 'None'}</p>
                    <p><strong>Treatment:</strong> {duplicateInfo.existing.treatment || 'None'}</p>
                  </div>
                )}
                {duplicateInfo.type === 'activity' && (
                  <div className="text-sm space-y-1">
                    <p><strong>Type:</strong> {duplicateInfo.existing.activity_type}</p>
                    <p><strong>Name:</strong> {duplicateInfo.existing.activity_name}</p>
                    <p><strong>Description:</strong> {duplicateInfo.existing.description || 'None'}</p>
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowConfirmDialog(false);
                  setDuplicateInfo(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={(e) => handleConfirmOverwrite(e)}
                className="btn-primary text-white"
              >
                Yes, Overwrite
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentProfile;
